import React from "react";

import styled from "styled-components";
import { Box } from "@mui/material";

import { ExpressionValue } from "../../rule-engine-core/Expressions/ExpressionValue";
import {
    ExpressionValueType,
    ObjectExpressionValueType,
    StringExpressionValueType,
} from "../../rule-engine-core/Expressions/ExpressionValueType";
import {
    ExpressionRuleRegistration,
    ExpressionRuleRegistrationData,
} from "../../rule-engine-core/Expressions/RegisterExpressionRule";
import { AutoBind } from "../../rule-engine-core/Common/AutoBind";

import { BackIcon } from "../../CommonUI/Icon/BackIcon";
import { ObserveState } from "../../CommonUI/StateManagement/ObserveState";
import VariablePicker from "../../CommonUI/VariablePicker/VariablePicker";
import { IDataDefinition } from "../../rule-engine-core/Common/Data/Definitions/IDataDefinition";
import { StateManager } from "../../rule-engine-core/Common/StateManager";
import { DataEditControl } from "../../CommonUI/DataEditing/DataEditControl";
import { StyledButton } from "../../CommonUI/BuildlingBlocks/StyledButton";
import { PlusGrayIcon } from "../../CommonUI/Icon/PlusGrayIcon";

import { InsertExpressionPage } from "./InsertExpressionPage";
import { ExpressionsGrid } from "./CommonUI";
import { ToolboxPanel } from "./ToolboxPanel";
import { AddExpressionButton } from "./AddExpressionButton";
import { ModeButtons } from "./ModeButtons";
import { ExpressionRule } from "../../rule-engine-core/Expressions/ExpressionRule";
import { ValidationError } from "./ValidationError";
import { UILayer } from "../../CommonUI/BuildlingBlocks/UILayer";
import { DataTypes } from "../../rule-engine-core/Common/Data/DataTypes";
import { StyledLink } from "../../CommonUI/BuildlingBlocks/StyledLink";
import { RadioOffIcon } from "../../CommonUI/Icon/RadioOffIcon";
import { RadioOnIcon } from "../../CommonUI/Icon/RadioOnIcon";
// import { isThisISOWeek } from "date-fns";
import { ConfirmDialog } from "../../CommonUI/ConfirmDialog";
import { DataUtilities } from "../../rule-engine-core/Common/DataUtilities";
import { ExpressionConstants } from "../../rule-engine-core/Expressions/ExpressionContants";
import { RenderExpressionRule } from "./RegisterExpressionRuleEditor";
import { UILabel } from "../../CommonUI/BuildlingBlocks/UILabel";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";
import { StyledToggleButton, StyledToggleButtonGroup } from "../../CommonUI/BuildlingBlocks/StyledToggleButonGroup";
import { MappingBuilder } from "../../Mapping/MappingBuilder";
import { MappingConfiguration } from "../../rule-engine-core/Mapping/MappingConfiguration";
import { TextMergeBuilder } from "../../TextMerge/TextMergeBuilder";
import { TextMergeConfiguration } from "../../rule-engine-core/TextMerge/TextMergeConfiguration";
import { TextField } from "@mui/material";
import { ExecutionData } from "../../rule-engine-core/Common/Data/ExecutionData/ExecutionData";
import type { AppGlueAny } from "../../rule-engine-core/Common/AppGlueAny";
import { UIButton } from "../../CommonUI/BuildlingBlocks/UIButton";

const ExpressionValueSlotEditor = styled.div`
    font-size: 16px;
    background: ${UIStyleConstants.WHITE_COLOR};
    box-shadow:
        4px 4px 4px ${UIStyleConstants.GRAY_VERY_LIGHT_COLOR},
        8px 8px 40px ${UIStyleConstants.GRAY_LIGHTER_COLOR};
    border-radius: 4px;
    background: ${UIStyleConstants.WHITE_COLOR};
    width: 650px;
    z-index: 101;
    max-height: none;
    padding: 16px 20px;
`;

const Header = styled.div`
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 16px;
    letter-spacing: 0em;
    text-align: left;

    width: 100%;
    display: flex;
    color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};

    align-items: center;
`;

const HeaderFloatRight = styled.div`
    display: flex;
    margin-left: auto;
    justify-content: flex-end;
    padding-right: 15px;
    align-items: center;

    & > * {
        margin-left: 8px !important;

        &:first-child {
            margin-left: 0 !important;
        }
    }
`;

const ExpressionColContainer = styled.div`
    display: flex;
    width: 100%;
    height: 332px;
    padding: 8px;
    flex-wrap: wrap;
`;

const ExpressionViewPanel = styled.div`
    width: 100%;
    max-height: 376px;
    min-height: 332px;
    padding: 20px 0;
    clear: both;
    display: flex;
    flex-direction: column;
`;

const ExpressionViewPanelLine = styled.div`
    width: 100%;
    justify-content: flex-start;
    clear: both;
    text-transform: none;

    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 17px;
    letter-spacing: 0px;
`;

const ExpressionViewPanelLineCenter = styled.div`
    position: relative;
    display: flex;
    margin-left: auto;
    align-items: center;
    clear: both;
    min-height: 120px;
    border-radius: 5px;
    flex: 1;
    overflow: auto;
    width: 100%;

    border: 1px solid ${UIStyleConstants.GRAY_LIGHTER_COLOR};
    box-sizing: border-box;
    border-radius: 4px;

    flex-direction: column;

    padding: 20px;
`;

const VariableOrValColumn = styled.div`
    border-left: 2px solid ${UIStyleConstants.GRAY_LIGHTER_COLOR};

    display: flex;
    justify-content: center;
    align-items: center;
    width: 33%;
    padding-left: 10px;
    padding-right: 10px;
`;

const ExpressionColumn = styled.div`
    width: 33%;
    padding-right: 12px;
`;

const VariableOrValContentPanel = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    padding-left: 10px;
    padding-right: 10px;
    height: 332px;

    .unset .MuiSwitch-switchBase {
        padding: 0px !important;
    }
`;

const VariableContent = styled.div`
    display: flex;
    flex-direction: column;

    .VariablePicker_Content {
        background: ${UIStyleConstants.WHITE_COLOR};
    }
`;

export class ExpressionValueDialog extends React.Component<
    { expressionValue: ExpressionValue; onClose?: () => void },
    {
        inserting: boolean;
        variableMode: "use" | "pick";
        openConfirmModal: boolean;
        objectDialogShowMapping: boolean;
        objectDialogShowCancelConfirm: boolean;
        stringEditorShowCancelConfirm: boolean;
        stringEditorShowMerge: boolean;
        stringEditorValue: string;
    }
> {
    constructor(
        props:
            | Readonly<{ expressionValue: ExpressionValue; position: { x: number; y: number } }>
            | { expressionValue: ExpressionValue; position: { x: number; y: number } },
    ) {
        super(props);

        this.state = {
            inserting: false,
            variableMode:
                this.props.expressionValue.variableName ===
                this.props.expressionValue.editContext?.definition?.requiredVariable?.name
                    ? "use"
                    : "pick",
            openConfirmModal: false,
            objectDialogShowMapping: false,
            objectDialogShowCancelConfirm: false,
            stringEditorShowCancelConfirm: false,
            stringEditorShowMerge: false,
            stringEditorValue: "",
        };
    }

    render() {
        return (
            <UILayer
                open={true}
                padding="0px"
                onClose={this.props.onClose}
                renderUI={() => {
                    return (
                        <ExpressionValueSlotEditor className="EditExpressionModal__root">
                            <ObserveState
                                listenTo={this.props.expressionValue.editContext?.expression}
                                control={() => {
                                    return (
                                        <ObserveState
                                            listenTo={this.props.expressionValue}
                                            properties={["valueTypeValue", "valueType"]}
                                            control={() => (
                                                <>
                                                    {this.renderHeader()}
                                                    {this.state.inserting ? (
                                                        <InsertExpressionPage
                                                            expressionValue={this.props.expressionValue}
                                                            onComplete={this.insertExpression}
                                                            onCancel={() => this.setState({ inserting: false })}
                                                        />
                                                    ) : (
                                                        this.renderPage()
                                                    )}
                                                </>
                                            )}
                                        />
                                    );
                                }}
                            />

                            <DialogActions>
                                <StyledLink
                                    onClick={() => {
                                        if (this.props.expressionValue.valueType !== ExpressionValueType.UNSET) {
                                            if (
                                                DataUtilities.getCookie(
                                                    ExpressionConstants.EXPRESSION_CLEAR_COOKIE_KEY,
                                                ) !== "true"
                                            )
                                                this.setState({ openConfirmModal: true });
                                            else this.props.expressionValue.clear();
                                        }
                                    }}
                                    style={{
                                        marginRight: 15,
                                    }}
                                    disabled={this.props.expressionValue.valueType === ExpressionValueType.UNSET}
                                >
                                    Clear
                                </StyledLink>

                                <StyledButton
                                    sx={{ background: UIStyleConstants.BLUE_COLOR }}
                                    variant="contained"
                                    onClick={this.handleClose}
                                >
                                    Complete
                                </StyledButton>
                            </DialogActions>
                        </ExpressionValueSlotEditor>
                    );
                }}
            />
        );
    }

    renderHeader() {
        return (
            <Header>
                <UILabel variant={"page_heading"}>Edit Expression Value</UILabel>
                <HeaderFloatRight>
                    {this.props.expressionValue.editContext?.getParentExpressionValue(this.props.expressionValue._id) &&
                        !this.state.inserting && (
                            <UIButton
                                size="small"
                                onClick={this.onSelectParent}
                                variant="outlined"
                            >
                                Select Parent
                            </UIButton>
                        )}
                    {!this.state.inserting && this.props.expressionValue.valueType !== ExpressionValueType.UNSET && (
                        <UIButton
                            size="small"
                            onClick={() => {
                                this.setState({ inserting: true });
                            }}
                            variant="outlined"
                        >
                            Insert Expression
                        </UIButton>
                    )}
                </HeaderFloatRight>
            </Header>
        );
    }

    renderConfirmDialog() {
        return (
            <ConfirmDialog
                open={this.state.openConfirmModal}
                action="clear"
                onConfirm={(dontAskAgain) => {
                    if (dontAskAgain) {
                        DataUtilities.setCookieValue(ExpressionConstants.EXPRESSION_CLEAR_COOKIE_KEY, "true");
                    }

                    this.props.expressionValue.clear();
                    this.setState({
                        openConfirmModal: false,
                    });
                }}
                onCancel={() => this.setState({ openConfirmModal: false })}
            />
        );
    }

    renderPage() {
        switch (this.props.expressionValue.valueType) {
            case ExpressionValueType.SUBEXPRESSION:
                return (
                    <>
                        <ModeButtons value={this.props.expressionValue} />
                        {
                            // no expression, render the toolbox
                            !this.props.expressionValue.subExpression ? (
                                <ToolboxPanel
                                    large={false}
                                    expressionValue={this.props.expressionValue}
                                    onExpressionSelected={(registration: ExpressionRuleRegistrationData) => {
                                        let exp = registration.create();
                                        this.props.expressionValue.subExpression = exp;
                                        this.props.expressionValue.editContext?.setSelection(
                                            this.props.expressionValue._id,
                                        );
                                    }}
                                />
                            ) : (
                                this.renderExpressionPanel()
                            )
                        }
                        {this.renderConfirmDialog()}
                    </>
                );
            case ExpressionValueType.VARIABLE:
                return (
                    <>
                        <ModeButtons value={this.props.expressionValue} />
                        <VariableOrValContentPanel>
                            <VariableContent>{this.renderVariableControl()}</VariableContent>
                        </VariableOrValContentPanel>
                        {this.renderConfirmDialog()}
                    </>
                );
            case ExpressionValueType.VALUE:
                if (
                    this.props.expressionValue.expectedType.type === DataTypes.STRING &&
                    this.props.expressionValue.expectedType.list === false
                ) {
                    // do string rendering
                    return (
                        <>
                            <ModeButtons value={this.props.expressionValue} />
                            <ObserveState
                                listenTo={this.props.expressionValue}
                                control={() => {
                                    return (
                                        <Box
                                            justifyContent={"top"}
                                            width={"100%"}
                                            paddingLeft={"10px"}
                                            paddingRight={"10px"}
                                            height={"332px"}
                                            display={"flex"}
                                            flexDirection={"column"}
                                            alignItems={"center"}
                                        >
                                            <Box
                                                alignItems={"center"}
                                                display={"flex"}
                                                flexDirection={"row"}
                                                paddingTop={"20px"}
                                            >
                                                <StyledToggleButtonGroup
                                                    value={this.props.expressionValue.stringValueType}
                                                    sx={{ background: UIStyleConstants.BLUE_COLOR }}
                                                    onChange={(
                                                        event: React.MouseEvent<HTMLElement>,
                                                        value: AppGlueAny,
                                                    ) => {
                                                        event.stopPropagation();
                                                        this.props.expressionValue.stringValueType = value;
                                                        StateManager.propertyChanged(
                                                            this.props.expressionValue,
                                                            "stringValueType",
                                                        );
                                                    }}
                                                    exclusive
                                                    style={{ marginBottom: 10 }}
                                                >
                                                    <StyledToggleButton value={StringExpressionValueType.SHORT}>
                                                        Single
                                                    </StyledToggleButton>
                                                    <StyledToggleButton value={StringExpressionValueType.LONG}>
                                                        Multi Line
                                                    </StyledToggleButton>
                                                    <StyledToggleButton value={StringExpressionValueType.MERGE}>
                                                        Text Merge
                                                    </StyledToggleButton>
                                                </StyledToggleButtonGroup>
                                            </Box>
                                            <Box
                                                justifyContent={"center"}
                                                width={"100%"}
                                                height={"100%"}
                                                display={"flex"}
                                                flexDirection={"column"}
                                                alignItems={"center"}
                                            >
                                                <this.RenderStringValueEditor />
                                            </Box>
                                        </Box>
                                    );
                                }}
                            />
                            {this.renderConfirmDialog()}
                        </>
                    );
                } else if (
                    this.props.expressionValue.expectedType.type === DataTypes.OBJECT &&
                    this.props.expressionValue.expectedType.list === false
                ) {
                    // do object rendering
                    return (
                        <>
                            <ModeButtons value={this.props.expressionValue} />
                            <ObserveState
                                listenTo={this.props.expressionValue}
                                control={() => {
                                    return (
                                        <Box
                                            justifyContent={"top"}
                                            width={"100%"}
                                            paddingLeft={"10px"}
                                            paddingRight={"10px"}
                                            height={"332px"}
                                            display={"flex"}
                                            flexDirection={"column"}
                                            alignItems={"center"}
                                        >
                                            <Box
                                                display={"flex"}
                                                flexDirection={"row"}
                                                paddingTop={"20px"}
                                            >
                                                <StyledToggleButtonGroup
                                                    value={this.props.expressionValue.objectValueType}
                                                    sx={{ background: UIStyleConstants.BLUE_COLOR }}
                                                    onChange={(
                                                        event: React.MouseEvent<HTMLElement>,
                                                        value: AppGlueAny,
                                                    ) => {
                                                        event.stopPropagation();
                                                        this.props.expressionValue.objectValueType = value;
                                                        StateManager.propertyChanged(
                                                            this.props.expressionValue,
                                                            "objectValueType",
                                                        );
                                                    }}
                                                    exclusive
                                                    style={{ marginBottom: 10 }}
                                                >
                                                    <StyledToggleButton value={ObjectExpressionValueType.CONSTANT}>
                                                        Constant
                                                    </StyledToggleButton>
                                                    <StyledToggleButton value={ObjectExpressionValueType.MAPPED}>
                                                        Mapped Value
                                                    </StyledToggleButton>
                                                </StyledToggleButtonGroup>
                                            </Box>
                                            <Box
                                                justifyContent={"center"}
                                                width={"100%"}
                                                height={"100%"}
                                                display={"flex"}
                                                flexDirection={"column"}
                                                alignItems={"center"}
                                            >
                                                <this.RenderObjectValueEditor />
                                            </Box>
                                        </Box>
                                    );
                                }}
                            />
                            {this.renderConfirmDialog()}
                        </>
                    );
                } else {
                    return (
                        <>
                            <ModeButtons value={this.props.expressionValue} />
                            <VariableOrValContentPanel>{this.renderValueControl()}</VariableOrValContentPanel>
                            {this.renderConfirmDialog()}
                        </>
                    );
                }

            case ExpressionValueType.UNSET:
                return (
                    <>
                        <ModeButtons value={this.props.expressionValue} />

                        <ExpressionColContainer>
                            <ExpressionColumn>
                                <ExpressionsGrid columnCount={2}>
                                    {ExpressionRuleRegistration.getCommonExpressions(
                                        this.props.expressionValue.editContext!.expression.getInputDefinition(),
                                    )
                                        .splice(0, 14)
                                        .map((expression, key) => (
                                            <AddExpressionButton
                                                key={key}
                                                expression={this.props.expressionValue}
                                                hideLabel={false}
                                                registration={expression}
                                                onClick={() => {
                                                    let exp = expression.create();
                                                    this.props.expressionValue.subExpression = exp;
                                                    this.props.expressionValue.editContext?.setSelection(
                                                        this.props.expressionValue._id,
                                                    );
                                                }}
                                            />
                                        ))}
                                </ExpressionsGrid>
                                <div style={{ marginTop: 10, textAlign: "center" }}>
                                    <StyledButton
                                        size="small"
                                        onClick={() => {
                                            this.props.expressionValue.valueType = ExpressionValueType.SUBEXPRESSION;
                                        }}
                                        startIcon={<PlusGrayIcon />}
                                    >
                                        Show More
                                    </StyledButton>
                                </div>
                            </ExpressionColumn>
                            <VariableOrValColumn>
                                <VariableContent>
                                    <StyledButton
                                        variant="outlined"
                                        data-testid="XExpressionValueDialog-pick-variableName"
                                        onClick={() => {
                                            this.props.expressionValue.valueType = ExpressionValueType.VARIABLE;
                                            this.props.expressionValue.editContext?.setSelection(
                                                this.props.expressionValue._id,
                                            );
                                        }}
                                    >
                                        Pick Data Name
                                    </StyledButton>
                                </VariableContent>
                            </VariableOrValColumn>
                            <VariableOrValColumn>
                                <StyledButton
                                    variant="outlined"
                                    data-testid="XExpressionValueDialog-pick-value"
                                    onClick={() => {
                                        this.props.expressionValue.valueType = ExpressionValueType.VALUE;
                                        this.props.expressionValue.editContext?.setSelection(
                                            this.props.expressionValue._id,
                                        );
                                    }}
                                >
                                    Enter Data Value
                                </StyledButton>
                            </VariableOrValColumn>
                        </ExpressionColContainer>
                    </>
                );
        }
    }

    @AutoBind
    RenderObjectValueEditor() {
        if (this.props.expressionValue.objectValueType === ObjectExpressionValueType.CONSTANT)
            return this.renderValueControl();

        // mapped
        return (
            <Box>
                <UILabel>{this.props.expressionValue.configDescription}</UILabel>
                <StyledButton
                    sx={{ background: UIStyleConstants.BLUE_COLOR }}
                    variant="contained"
                    onClick={() => {
                        this.setState({
                            objectDialogShowMapping: true,
                        });
                    }}
                >
                    Edit Mapping
                </StyledButton>

                <UILayer
                    fullScreen={true}
                    open={this.state.objectDialogShowMapping}
                    onClose={() => {
                        this.setState({
                            objectDialogShowMapping: false,
                        });
                    }}
                    renderUI={() => {
                        return (
                            <MappingBuilder
                                mappingBuilderName={"Expression Mapping"}
                                onSave={(data) => {
                                    let mappedFields = data.mappingRules.filter((m) => {
                                        return (
                                            m.mapping.rootExpression.expressionRoot.valueType !==
                                            ExpressionValueType.UNSET
                                        );
                                    }).length;

                                    if (mappedFields === 0)
                                        this.props.expressionValue.configDescription = "No Mapping Defined";
                                    else if (mappedFields === 1)
                                        this.props.expressionValue.configDescription = mappedFields + " Mapped Field";
                                    else this.props.expressionValue.configDescription = mappedFields + " Mapped Fields";

                                    this.props.expressionValue.mappingData = data;

                                    this.setState({
                                        objectDialogShowMapping: false,
                                    });
                                }}
                                onCancel={() => {
                                    if (DataUtilities.getCookie("CLOSE_EXPRESSION_VALUE_WITHOUT_SAVING") !== "true") {
                                        this.setState({
                                            objectDialogShowMapping: false,
                                        });
                                    } else {
                                        this.setState({
                                            objectDialogShowCancelConfirm: true,
                                        });
                                    }
                                }}
                                definition={this.props.expressionValue.getMappingClone() as MappingConfiguration}
                            />
                        );
                    }}
                ></UILayer>

                <ConfirmDialog
                    action={"Close"}
                    caption={"Without Saving"}
                    onConfirm={(dontAskAgain) => {
                        if (dontAskAgain) {
                            DataUtilities.setCookieValue("CLOSE_EXPRESSION_VALUE_WITHOUT_SAVING", "true");
                        }

                        this.setState({
                            objectDialogShowMapping: false,
                        });
                        this.setState({
                            objectDialogShowCancelConfirm: false,
                        });
                    }}
                    onCancel={() => {
                        this.setState({
                            objectDialogShowCancelConfirm: false,
                        });
                    }}
                    open={this.state.objectDialogShowCancelConfirm}
                ></ConfirmDialog>
            </Box>
        );
    }

    @AutoBind
    RenderStringValueEditor() {
        if (this.props.expressionValue.stringValueType === StringExpressionValueType.SHORT) {
            return this.renderValueControl();
        }

        // todo: render longer string here
        if (this.props.expressionValue.stringValueType === StringExpressionValueType.LONG) {
            return (
                <Box width={"550px"}>
                    <ControlLabel isCenter={this.props.expressionValue.expectedType.type === DataTypes.BOOLEAN}>
                        Enter Data Value
                    </ControlLabel>
                    <TextField
                        key={"textfield-expression"}
                        value={this.state.stringEditorValue}
                        onChange={(event) => {
                            this.setState({
                                stringEditorValue: event.target.value,
                            });
                            this.props.expressionValue.setValueWithoutRefresh(event.target.value);
                        }}
                        fullWidth={true}
                        multiline={true}
                        rows={7}
                        //autoFocus={true}
                    ></TextField>
                </Box>
            );
        }

        // merge
        return (
            <Box>
                <UILabel>{this.props.expressionValue.configDescription}</UILabel>
                <StyledButton
                    sx={{ background: UIStyleConstants.BLUE_COLOR }}
                    variant="contained"
                    onClick={() => {
                        this.setState({
                            stringEditorShowMerge: true,
                        });
                    }}
                >
                    Edit Text Merge
                </StyledButton>

                <UILayer
                    fullScreen={true}
                    open={this.state.stringEditorShowMerge}
                    onClose={() => {
                        this.setState({
                            stringEditorShowMerge: false,
                        });
                    }}
                    renderUI={() => {
                        return (
                            <TextMergeBuilder
                                allowTextTypeSwitch={true}
                                textMergeName={"Expression Text Merge"}
                                onSave={(data) => {
                                    let executionData: ExecutionData = new ExecutionData();
                                    executionData.set(TextMergeConfiguration.DEFAULT_VALUE_DATA_NAME, "[Field]");

                                    let res = data.run(executionData, undefined);

                                    res = DataUtilities.convertHTMLToString(res);

                                    this.props.expressionValue.configDescription = res;

                                    this.props.expressionValue.stringMergeData = data;

                                    this.setState({
                                        stringEditorShowMerge: false,
                                    });
                                }}
                                onCancel={() => {
                                    if (DataUtilities.getCookie("CLOSE_EXPRESSION_VALUE_WITHOUT_SAVING") !== "true") {
                                        this.setState({
                                            stringEditorShowMerge: false,
                                        });
                                    } else {
                                        this.setState({
                                            stringEditorShowCancelConfirm: true,
                                        });
                                    }
                                }}
                                definition={this.props.expressionValue.getMergeClone() as TextMergeConfiguration}
                            />
                        );
                    }}
                ></UILayer>

                <ConfirmDialog
                    action={"Close"}
                    caption={"Without Saving"}
                    onConfirm={(dontAskAgain) => {
                        if (dontAskAgain) {
                            DataUtilities.setCookieValue("CLOSE_EXPRESSION_VALUE_WITHOUT_SAVING", "true");
                        }

                        this.setState({
                            stringEditorShowCancelConfirm: false,
                            stringEditorShowMerge: false,
                        });
                    }}
                    onCancel={() => {
                        this.setState({
                            stringEditorShowCancelConfirm: false,
                            stringEditorShowMerge: false,
                        });
                    }}
                    open={this.state.stringEditorShowCancelConfirm}
                ></ConfirmDialog>
            </Box>
        );
    }

    renderValueControl() {
        return (
            <ValueRender data-testid="ValueDialog-valueEditor">
                <ControlLabel isCenter={this.props.expressionValue.expectedType.type === DataTypes.BOOLEAN}>
                    Enter Data Value
                </ControlLabel>
                <DataEditControl
                    forData={this.props.expressionValue.expectedType}
                    value={this.props.expressionValue.value}
                    onUpdate={this.valueChange}
                    mode="edit_mode"
                />
            </ValueRender>
        );
    }

    @AutoBind
    canRenderVariablePicker() {
        if (!this.props.expressionValue.editContext?.definition?.requiredVariable) return true;
        else if (
            this.state.variableMode === "pick" &&
            this.props.expressionValue.editContext?.definition?.requiredVariable?.name !==
                this.props.expressionValue?.variableName
        )
            return true;
        return false;
    }

    @AutoBind
    renderVariableControl() {
        const dataDefinition = this.props.expressionValue.editContext!.expression.getInputDefinition();

        if (this.props.expressionValue.editContext?.definition?.requiredVariable) {
            return (
                <>
                    <UseOrPickRender>
                        <UseOrPickItemRender
                            selected={this.state.variableMode === "use"}
                            onClick={() => {
                                if (this.props.expressionValue.editContext?.definition?.requiredVariable) {
                                    this.props.expressionValue.variableName =
                                        this.props.expressionValue.editContext?.definition?.requiredVariable?.name;
                                }
                                this.handleClose();
                            }}
                        >
                            <Box
                                display="flex"
                                justifyContent={"space-between"}
                                alignItems="flex-start"
                                width={"100%"}
                            >
                                <UseOrPickTitle>Use Suggested Data Name</UseOrPickTitle>
                                {this.state.variableMode === "use" ? <RadioOnIcon /> : <RadioOffIcon />}
                            </Box>

                            <RequiredVariableRender>
                                {this.props.expressionValue.editContext?.definition?.requiredVariable?.name}
                            </RequiredVariableRender>
                        </UseOrPickItemRender>
                        <span>or</span>
                        <UseOrPickItemRender
                            selected={this.state.variableMode === "pick"}
                            onClick={() => this.setState({ variableMode: "pick" })}
                        >
                            <Box
                                display="flex"
                                justifyContent={"space-between"}
                                alignItems="flex-start"
                                width={"100%"}
                            >
                                <UseOrPickTitle>
                                    {this.props.expressionValue.editContext!.expression?.canChangeInputs
                                        ? `Pick or Create Data Name`
                                        : `Pick Data Name`}
                                </UseOrPickTitle>
                                {this.state.variableMode === "pick" ? <RadioOnIcon /> : <RadioOffIcon />}
                            </Box>

                            <VariablePicker
                                data={dataDefinition}
                                onVariableSet={(path: string, def?: IDataDefinition) => {
                                    if (def) {
                                        dataDefinition.fields.push(def);
                                        this.props.expressionValue.variableName = def.name;
                                        StateManager.propertyChanged(dataDefinition, "fields");
                                    } else {
                                        this.props.expressionValue.variableName = path;
                                    }
                                }}
                                selected={this.state.variableMode ? this.props.expressionValue.variableName : undefined}
                                allowNewVariable={this.props.expressionValue.editContext!.expression?.canChangeInputs}
                                requiredType={this.props.expressionValue.expectedType.type}
                                isList={this.props.expressionValue.expectedType.list}
                            />
                        </UseOrPickItemRender>
                    </UseOrPickRender>
                </>
            );
        }

        return (
            <>
                <ControlLabel>
                    {this.props.expressionValue.editContext!.expression?.canChangeInputs
                        ? `Pick or Create Data Name`
                        : `Pick Data Name`}
                </ControlLabel>
                <VariablePicker
                    data={dataDefinition}
                    size={"small"}
                    onVariableSet={(path: string, def?: IDataDefinition) => {
                        if (def) {
                            dataDefinition.fields.push(def);
                            this.props.expressionValue.variableName = def.name;
                            StateManager.propertyChanged(dataDefinition, "fields");
                        } else {
                            this.props.expressionValue.variableName = path;
                        }
                    }}
                    selected={this.props.expressionValue.variableName}
                    allowNewVariable={this.props.expressionValue.editContext!.expression?.canChangeInputs}
                    startOpenIfEmptyOrInvalid
                    requiredType={this.props.expressionValue.expectedType.type}
                    isList={this.props.expressionValue.expectedType.list}
                />
            </>
        );
    }

    @AutoBind
    private handleClose() {
        this.props.expressionValue.editContext?.clearSelection();

        switch (this.props.expressionValue.valueType) {
            case ExpressionValueType.VARIABLE:
                if (!this.props.expressionValue.variableName) {
                    if (
                        this.state.variableMode === "use" &&
                        this.props.expressionValue.editContext?.definition?.requiredVariable
                    ) {
                        this.props.expressionValue.variableName =
                            this.props.expressionValue.editContext?.definition?.requiredVariable?.name;
                    } else {
                        this.props.expressionValue.valueType = ExpressionValueType.UNSET;
                    }
                }
                break;
            case ExpressionValueType.VALUE:
                if (this.props.expressionValue.value === null || this.props.expressionValue.value === undefined)
                    this.props.expressionValue.valueType = ExpressionValueType.UNSET;
                break;
            case ExpressionValueType.SUBEXPRESSION:
                if (!this.props.expressionValue.subExpression)
                    this.props.expressionValue.valueType = ExpressionValueType.UNSET;
                break;
        }

        this.props.expressionValue.editContext?.clearSelection();
        this.props.expressionValue.editContext?.refresh();
    }

    @AutoBind
    private onSelectParent() {
        let parent = this.props.expressionValue.editContext?.getParentExpressionValue(this.props.expressionValue._id);
        if (parent) {
            this.props.expressionValue.editContext?.setSelection(parent._id);
        }
    }

    @AutoBind
    private insertExpression(position: ExpressionValue, inserted: ExpressionRule, selected: string) {
        // pick expression, pick what slot in expression you are.
        let ev = inserted.expressionContext?.getElement(selected) as ExpressionValue;
        if (!ev) throw new Error("could not find expression value");

        switch (position.valueType) {
            case ExpressionValueType.SUBEXPRESSION:
                ev.subExpression = position.subExpression;
                break;
            case ExpressionValueType.VALUE:
                ev.value = position.value;
                break;
            case ExpressionValueType.VARIABLE:
                ev.variableName = position.variableName;
                break;
        }

        position.subExpression = inserted;
        position.editContext?.setSelection(position._id);

        this.setState({
            inserting: false,
        });
    }

    @AutoBind
    private valueChange(newValue: AppGlueAny) {
        this.props.expressionValue.value = newValue;
        this.forceUpdate();
    }

    private renderExpressionPanel() {
        let type = Reflect.get(this.props.expressionValue.subExpression!, "__type");
        const expression = ExpressionRuleRegistration.getExpressionByType(
            type,
            this.props.expressionValue.editContext!.expression.getInputDefinition(),
        );

        return (
            <>
                {this.props.expressionValue.subExpression ? (
                    <ExpressionViewPanel>
                        <ExpressionViewPanelLine>
                            <StyledButton
                                size="small"
                                onClick={() => {
                                    this.props.expressionValue.subExpression = undefined;
                                    this.props.expressionValue.editContext?.setSelection(
                                        this.props.expressionValue._id,
                                    );
                                }}
                                data-testid="XExpressionValueDialog-reset-expression"
                                startIcon={<BackIcon />}
                            >
                                {expression?.name}
                            </StyledButton>
                        </ExpressionViewPanelLine>

                        <ExpressionViewPanelLineCenter>
                            <SubExpressionRender>
                                {RenderExpressionRule(this.props.expressionValue.subExpression)}
                                {<ValidationError value={this.props.expressionValue} />}
                            </SubExpressionRender>
                        </ExpressionViewPanelLineCenter>
                    </ExpressionViewPanel>
                ) : (
                    <></>
                )}
            </>
        );
    }
}

const ValueRender = styled.div`
    .XValueEditor-editWrapper {
        justify-content: center;
        display: flex;
    }
`;

const DialogActions = styled.div`
    padding: 20px 0 0;
    border-top: solid 1px ${UIStyleConstants.GRAY_LIGHTER_COLOR};
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const UseOrPickRender = styled.div`
    display: flex;
    align-items: center;

    //styleName: text/Inter Medium 12px;

    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 17px;
    letter-spacing: 0px;
    text-align: center;

    color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};

    > * {
        margin-left: 8px;

        &:first-child {
            margin-left: 0;
        }
    }
`;

const UseOrPickItemRender = styled.div<{ selected: boolean }>`
    background: ${UIStyleConstants.GRAY_LIGHT_COLOR};
    /* main/border */

    border: 1px solid
        ${(props) => (props.selected ? UIStyleConstants.BLUE_COLOR : "" + UIStyleConstants.GRAY_LIGHT_COLOR)};
    box-sizing: border-box;
    border-radius: 4px;

    display: flex;
    flex-direction: column;

    align-items: center;

    padding: 16px 16px 20px 20px;

    width: 210px;
    min-height: 110px;

    position: relative;
`;

const UseOrPickTitle = styled.div`
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 17px;
    letter-spacing: 0px;
    text-align: center;
    color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};

    margin-bottom: 16px;
`;

const RequiredVariableRender = styled.div`
    min-width: 93px;
    height: 41px;
    padding: 0 16px;
    background: ${UIStyleConstants.GRAY_LIGHT_COLOR};

    //styleName: text/Inter Medium 14px;

    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px;
    letter-spacing: 0em;
    text-align: left;
    color: ${UIStyleConstants.BLUE_COLOR};

    display: flex;
    align-items: center;
    justify-content: center;

    border: 1px solid ${UIStyleConstants.GRAY_LIGHTER_COLOR};
    box-sizing: border-box;
    border-radius: 4px;
`;

const SubExpressionRender = styled.div`
    position: relative;

    margin: auto;
`;

const ControlLabel = styled.div<{ isCenter?: boolean }>`
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px;
    letter-spacing: 0em;
    text-align: ${(props) => (props.isCenter ? "center" : "left")};
    color: ${UIStyleConstants.BLUE_COLOR};
    margin-bottom: 10px;
`;
